<template>
    <v-card>
        <material-header
                title="Data Variable"
                toolbar-color="grey darken-4"
                :info="`Statistics for ${dataVariable.name}`"
                :dense="true"
                :loading="statsLoading"
                :icon="dataVariableIcon">

            <template slot="buttons">
                <v-btn icon text @click="closeClicked()">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </material-header>

        <v-card-text class="pa-2">

            <v-container fluid class="pa-0">
                <v-row dense>
                    <v-col lg="3" md="6" cols="12" class="pt-0">

                        <!-- INNER LAYOUT -->
                        <v-row dense>
                            <v-col cols="12">
                                <stat-box
                                        :value="dataCount"
                                        hint="Number of records"
                                        color="primary"
                                        :loading="statsLoading"
                                ></stat-box>
                            </v-col>

                            <v-col cols="12">
                                <stat-box
                                        :value="dataMissingCount"
                                        hint="Number of records missing"
                                        color="warning"
                                        :loading="statsLoading"
                                ></stat-box>
                            </v-col>

                        </v-row>

                    </v-col>

                    <v-col lg="6" md="6" cols="12" class="pt-0" v-if="ageStatsEnabled">

                        <v-row dense>

                            <v-col cols="12">
                                <v-card raised color="primary" :height="210">

                                    <v-card-text class="pa-0">

                                        <v-sparkline
                                                v-if="ageByMonth.length > 0"
                                                :smooth="16"
                                                :labels="ageLabels"
                                                :line-width="2"
                                                :value="ageCounts"
                                                color="white"
                                                auto-draw
                                                :padding="16"
                                                stroke-linecap="round"
                                                :height="95"
                                        ></v-sparkline>
                                    </v-card-text>

                                    <v-card-text class="pt-0 pl-3">
                                        Variable age distribution (months)
                                    </v-card-text>
                                </v-card>

                            </v-col>
                        </v-row>
                    </v-col>

                    <v-col lg="3" md="6" cols="12" class="pt-0">

                        <!-- INNER LAYOUT -->
                        <v-row dense>

                            <v-col cols="12" v-if="ageStatsEnabled">

                                <stat-box
                                        :value="ageMean"
                                        hint="Variable mean age in months"
                                        color="primary"
                                        :loading="statsLoading"
                                ></stat-box>

                            </v-col>

                            <v-col cols="12" v-show="isKeyword">

                                <stat-box
                                        v-if="uniqueValueCount"
                                        :value="uniqueValueCount"
                                        hint="Unique keyword count"
                                        color="primary"
                                        :loading="statsLoading"
                                ></stat-box>

                            </v-col>

                        </v-row>
                    </v-col>

                    <!-- CHART -->
                    <v-col cols="12" class="pt-0" v-if="isMultipleChoice">
                        <multiple-choice-stats :data-variable="dataVariable"></multiple-choice-stats>
                    </v-col>

                    <!-- NUMBER STATS -->
                    <v-col cols="12" class="pt-0" v-if="isNumber">
                        <number-stats :data-variable="dataVariable"></number-stats>
                    </v-col>

                    <!-- DATE STATS -->
                    <v-col cols="12" class="pt-0" v-if="isDate">
                        <date-stats :data-variable="dataVariable"></date-stats>
                    </v-col>

                    <!-- KEYWORD -->
                    <v-col cols="12" class="pt-0" v-if="isKeyword">
                        <keyword-stats :data-variable="dataVariable"></keyword-stats>
                    </v-col>

                    <!-- LOCALE -->
                    <v-col cols="12" class="pt-0" v-if="isLocale">
                        <locale-stats :data-variable="dataVariable"></locale-stats>
                    </v-col>

                </v-row>
            </v-container>

            <json v-if="debug" :data="ageByMonth"></json>

        </v-card-text>
    </v-card>
</template>

<script>

    import {mapState, mapActions} from 'vuex';
    import activeSessionChecker from '../../../../components/mixins/activeSessionCheckerMixin';
    import MaterialHeader from '../../../../components/UI/MaterialHeader';
    import StatBox from '../../../../components/UI/StatBoxNumerical';
    import Locale from '../../../../components/UI/Locale';
    import Json from 'vue-json-pretty';
    import DataVariable from '../../ts/DataVariableInterface';
    import MultipleChoiceStats from './MultipleChoiceStats';
    import NumberStats from './NumberStats';
    import DateStats from './DateStats';
    import KeywordStats from "./KeywordStats";
    import LocaleStats from "./LocaleStats";

    export default {

        mixins: [activeSessionChecker],

        components: {
            KeywordStats,
            MaterialHeader,
            StatBox,
            Locale,
            Json,
            MultipleChoiceStats,
            NumberStats,
            DateStats,
            LocaleStats
        },

        props: {
            dataVariable: {
                required: true,
                type: DataVariable
            }
        },

        data() {
            return {
                debug: false,
                statsLoading: false,
                dataCount: 0,
                dataMissingCount: 0,
                ageByMonth: [],
                ageMean: 0,
                ageStatsEnabled: false,
                uniqueValueCount: 0,
                chart: null,
            }
        },

        computed: {
            ...mapState(['dataVariableIcons']),
            ...mapState('PanelSettings', ['settings']),

            isDate() {
                return this.dataVariable.type === 'date';
            },

            isNumber() {
                return this.dataVariable.type === 'number';
            },

            isKeyword() {
                return this.dataVariable.type === 'keyword';
            },

            isLocale() {
                return this.dataVariable.type === 'locale';
            },

            isMultipleChoice() {
                return ['radio','checkbox'].includes(this.dataVariable.type);
            },

            ageCounts() {
                return this.ageByMonth.map(item => item.count);
            },

            ageLabels() {
                return this.ageByMonth.map(item => "" + item.age)
            },

            dataVariableIcon() {
                return this.dataVariableIcons[this.dataVariable.type];
            },
        },

        mounted() {
            this.statsLoading = true;

            this.getDataVariableDataCount(this.dataVariable.id)
                .then(data => {
                    this.dataCount = data.count;
                    this.dataMissingCount = data.missing;

                    if (data.ageByMonth) {
                        this.ageByMonth = data.ageByMonth;
                        this.ageMean = data.ageMean;
                        this.ageStatsEnabled = true;
                    }

                    this.uniqueValueCount = data.uniqueKeywordCount ? data.uniqueKeywordCount : null;
                })
                .catch(() => {
                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'Failed loading stored data count for the option!'
                    });
                })
                .finally(() => {
                    this.statsLoading = false;
                });
        },

        methods: {

            // Root actions
            ...mapActions([
                'getDataVariableDataCount'
            ]),

            closeClicked() {
                this.$emit('close');
            },
        }
    }
</script>
